import * as React from "react";
import { CalendarIcon, ChevronLeft, ChevronRight } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface DateInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
}

export function DateInput({ label, className, value, onChange }: DateInputProps) {
  const [isOpen, setIsOpen] = React.useState(false);
  const [currentDate, setCurrentDate] = React.useState(new Date(2025, 9)); // October 2025
  const containerRef = React.useRef<HTMLDivElement>(null);

  const parseYYYYMMDDToLocalDate = (dateStr: string) => {
    // Avoid `new Date("YYYY-MM-DD")` which parses as UTC and can shift dates.
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
    if (!m) return null;
    const y = Number(m[1]);
    const mo = Number(m[2]);
    const d = Number(m[3]);
    if (!y || !mo || !d) return null;
    return new Date(y, mo - 1, d);
  };

  const formatLocalDateToYYYYMMDD = (d: Date) => {
    const yyyy = String(d.getFullYear()).padStart(4, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const selectedDate = value ? parseYYYYMMDDToLocalDate(String(value)) : null;

  const getDaysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const handleSelectDate = (day: number) => {
    const newDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    const dateString = formatLocalDateToYYYYMMDD(newDate);
    onChange?.({ currentTarget: { value: dateString } } as any);
    setIsOpen(false);
  };

  const daysInMonth = getDaysInMonth(currentDate);
  const firstDay = getFirstDayOfMonth(currentDate);
  const days = [];

  // Add empty cells for days before month starts
  for (let i = 0; i < firstDay; i++) {
    const prevMonth = currentDate.getMonth() - 1;
    const prevYear = prevMonth < 0 ? currentDate.getFullYear() - 1 : currentDate.getFullYear();
    const prevMonthDays = getDaysInMonth(new Date(prevYear, prevMonth < 0 ? 11 : prevMonth));
    days.push({
      day: prevMonthDays - firstDay + i + 1,
      isCurrentMonth: false,
      isSelected: false,
    });
  }

  // Add days of current month
  for (let i = 1; i <= daysInMonth; i++) {
    days.push({
      day: i,
      isCurrentMonth: true,
      isSelected: selectedDate?.getDate() === i && selectedDate?.getMonth() === currentDate.getMonth() && selectedDate?.getFullYear() === currentDate.getFullYear(),
    });
  }

  // Add empty cells for days after month ends
  const totalCells = Math.ceil(days.length / 7) * 7;
  for (let i = days.length; i < totalCells; i++) {
    days.push({
      day: i - days.length + 1,
      isCurrentMonth: false,
      isSelected: false,
    });
  }

  const monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  const dayNames = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const formatDate = (dateStr: string | number | readonly string[]) => {
    if (!dateStr) return '';
    const date = parseYYYYMMDDToLocalDate(String(dateStr));
    if (!date) return String(dateStr);
    return date.toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric' });
  };

  return (
    <div className="space-y-2" ref={containerRef}>
      <Label>{label}</Label>
      <div className="relative">
        <Button
          type="button"
          variant="outline"
          className={cn("w-full h-9 justify-start font-normal text-foreground", className)}
          onClick={() => setIsOpen(!isOpen)}
        >
          <CalendarIcon className="mr-2 h-4 w-4 text-muted-foreground" />
          <span className="flex-1 text-left">{value ? formatDate(value as string) : "Select start date"}</span>
        </Button>

        {isOpen && (
          <div className="absolute z-50 mt-2 w-64 rounded-lg border bg-background p-4 shadow-lg">
            {/* Calendar Header */}
            <div className="mb-4 flex items-center justify-between">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={handlePrevMonth}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="text-sm font-semibold">
                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                onClick={handleNextMonth}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>

            {/* Day names */}
            <div className="mb-2 grid grid-cols-7 gap-1 text-center text-xs font-medium text-muted-foreground">
              {dayNames.map((day) => (
                <div key={day} className="h-7 flex items-center justify-center">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">
              {days.map((d, idx) => (
                <button
                  key={idx}
                  type="button"
                  className={cn(
                    "h-8 rounded text-sm font-medium transition-colors",
                    d.isCurrentMonth ? "text-foreground cursor-pointer hover:bg-accent" : "text-muted-foreground cursor-default",
                    d.isSelected && d.isCurrentMonth ? "bg-primary text-primary-foreground hover:bg-primary" : ""
                  )}
                  onClick={() => d.isCurrentMonth && handleSelectDate(d.day)}
                  disabled={!d.isCurrentMonth}
                >
                  {d.day}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


